function locationsFilter(locations = []) {
    return {
        // Form state (applied)
        suburbPostcode: '',
        suburbPostcodeText: '',
        suburbLatLng: null,
        radius: 5,
        selectedStates: [],

        // Pending (form inputs)
        pendingRadius: 5,
        pendingSuburbLatLng: null,
        pendingSuburbPostcodeText: '',

        locations,
        filteredLocations: [],

        // Pagination
        page: 1,

        // Guard flag
        isInternalUpdate: false,

        resetToDefault() {
            this.suburbPostcode = '';
            this.suburbPostcodeText = '';
            this.suburbLatLng = null;
            this.radius = 5;
            this.selectedStates = [];

            this.pendingRadius = 5;
            this.pendingSuburbLatLng = null;
            this.pendingSuburbPostcodeText = '';

            this.page = 1;
        },

        init() {
            this.loadFromQuery();

            // Sync pending with initial values
            this.pendingRadius = this.radius;
            this.pendingSuburbLatLng = this.suburbLatLng;
            this.pendingSuburbPostcodeText = this.suburbPostcodeText;

            this.filterLocations();

            // ✅ selectedStates still instant
            this.$watch('selectedStates', () => {
                if (this.isInternalUpdate) return;

                this.isInternalUpdate = true;

                this.suburbPostcode = '';
                this.suburbPostcodeText = '';
                this.suburbLatLng = null;

                this.pendingSuburbLatLng = null;
                this.pendingSuburbPostcodeText = '';

                this.onFilterChange();

                this.$nextTick(() => (this.isInternalUpdate = false));
            });

            // Clear logic when postcode cleared
            this.$watch('suburbPostcode', (value) => {
                if (this.isInternalUpdate) return;

                if (!value || value === '') {
                    this.isInternalUpdate = true;

                    this.resetToDefault();

                    if (this.$refs.postcodeInput) {
                        this.$refs.postcodeInput.value = '';
                    }

                    this.filterLocations();
                    this.updateQuery();

                    this.$nextTick(() => (this.isInternalUpdate = false));
                }
            });

            this.$watch('page', () => this.updateQuery());

            const initAutocompleteSafe = () => {
                if (window.google && this.$refs.postcodeInput) {
                    this.initAutocomplete();

                    if (this.suburbPostcodeText) {
                        this.$refs.postcodeInput.value = this.suburbPostcodeText;
                    }
                    return true;
                }
                return false;
            };

            if (!initAutocompleteSafe()) {
                const interval = setInterval(() => {
                    if (initAutocompleteSafe()) clearInterval(interval);
                }, 100);
            }

            if (window.location.search) {
                this.$nextTick(() => {
                    this.scrollToResults();
                });
            }
        },

        scrollToResults() {
            document.querySelector('#results')?.scrollIntoView({ behavior: 'smooth' });
        },

        // ✅ NEW: submit-based filtering
        applyFilters() {
            this.isInternalUpdate = true;

            this.radius = this.pendingRadius || 5;
            this.suburbLatLng = this.pendingSuburbLatLng;
            this.suburbPostcodeText = this.pendingSuburbPostcodeText;

            if (this.suburbLatLng) {
                this.selectedStates = [];
            }

            this.page = 1;

            this.filterLocations();
            this.updateQuery();

            this.$nextTick(() => {
                this.isInternalUpdate = false;
                this.scrollToResults();
            });
        },

        clearFilters() {
            this.isInternalUpdate = true;

            // Reset applied values
            this.suburbPostcode = '';
            this.suburbPostcodeText = '';
            this.suburbLatLng = null;
            this.radius = 5;
            this.selectedStates = [];

            // Reset pending values (important for UI inputs)
            this.pendingRadius = 5;
            this.pendingSuburbLatLng = null;
            this.pendingSuburbPostcodeText = '';

            // Clear input field visually
            if (this.$refs.postcodeInput) {
                this.$refs.postcodeInput.value = '';
            }

            // Reset pagination
            this.page = 1;

            // Re-run filtering with defaults
            this.filterLocations();

            // Clear URL params
            this.updateQuery();

            this.$nextTick(() => {
                this.isInternalUpdate = false;
            });
        },

        hasSuburb() {
            return !!(this.pendingSuburbPostcodeText || this.suburbPostcodeText);
        },

        hasSuburbApplied() {
            return !!(this.suburbPostcodeText);
        },

        // Used only for state changes now
        onFilterChange() {
            this.page = 1;
            this.filterLocations();
            this.updateQuery();
        },

        loadFromQuery() {
            const params = new URLSearchParams(window.location.search);

            const states = params.get('state');
            const radius = params.get('radius');
            const lat = params.get('lat');
            const lng = params.get('lng');
            const suburbText = params.get('suburb');
            const page = params.get('page');

            if (states) this.selectedStates = states.split(',');
            if (radius) this.radius = parseFloat(radius);

            if (lat && lng) {
                this.suburbLatLng = {
                    lat: parseFloat(lat),
                    lng: parseFloat(lng)
                };
            }

            if (suburbText) this.suburbPostcodeText = suburbText;
            if (page) this.page = parseInt(page);
        },

        updateQuery() {
            const params = new URLSearchParams();

            if (this.selectedStates.length) {
                params.set('state', this.selectedStates.join(','));
            }

            if (this.radius) params.set('radius', this.radius);

            if (this.suburbLatLng) {
                params.set('lat', this.suburbLatLng.lat);
                params.set('lng', this.suburbLatLng.lng);
            }

            if (this.suburbPostcodeText) {
                params.set('suburb', this.suburbPostcodeText);
            }

            if (this.page && this.page > 1) {
                params.set('page', this.page);
            }

            const newUrl =
                window.location.pathname +
                (params.toString() ? `?${params.toString()}` : '');

            window.history.replaceState({}, '', newUrl);
        },

        initAutocomplete() {
            const autocomplete = new google.maps.places.Autocomplete(
                this.$refs.postcodeInput,
                {
                    types: ['(regions)'],
                    componentRestrictions: { country: 'AU' }
                }
            );

            autocomplete.addListener('place_changed', () => {
                const place = autocomplete.getPlace();

                if (place.geometry) {
                    this.pendingSuburbLatLng = {
                        lat: place.geometry.location.lat(),
                        lng: place.geometry.location.lng()
                    };

                    this.pendingSuburbPostcodeText =
                        place.formatted_address || '';
                }
            });

            if (this.suburbPostcodeText && this.$refs.postcodeInput) {
                this.$refs.postcodeInput.value = this.suburbPostcodeText;
            }
        },

        handleStateSelection(value) {
            if (this.selectedStates.includes(value)) {
                this.selectedStates = this.selectedStates.filter(v => v !== value);
            } else {
                this.selectedStates = [...this.selectedStates, value];
            }

            this.isInternalUpdate = true;

            this.suburbPostcode = '';
            this.suburbPostcodeText = '';
            this.suburbLatLng = null;

            this.pendingSuburbLatLng = null;
            this.pendingSuburbPostcodeText = '';

            this.$nextTick(() => {
                this.isInternalUpdate = false;
            });

            this.onFilterChange();
        },

        filterLocations() {
            let filtered = this.locations;

            if (this.selectedStates.length > 0) {
                filtered = filtered.filter(l =>
                    this.selectedStates.includes(this.toKebabCase(l.state))
                );
            }

            if (this.suburbLatLng) {
                const radius = parseFloat(this.radius) || 5;

                filtered = filtered
                    .map(l => {
                        if (
                            !l.geolocation ||
                            l.geolocation.lat == null ||
                            l.geolocation.lng == null
                        ) return null;

                        const lat1 = Number(l.geolocation.lat);
                        const lng1 = Number(l.geolocation.lng);
                        const lat2 = Number(this.suburbLatLng.lat);
                        const lng2 = Number(this.suburbLatLng.lng);

                        const distance = this.getDistance(lat1, lng1, lat2, lng2);

                        if (isNaN(distance)) return null;

                        return {
                            ...l,
                            id: String(l.id),
                            distance: Math.round(distance * 10) / 10
                        };
                    })
                    .filter(l => l && l.distance <= radius);

                filtered.sort((a, b) => a.distance - b.distance);
            } else {
                filtered = filtered.map(l => ({
                    ...l,
                    id: String(l.id),
                    distance: null
                }));
            }

            this.filteredLocations = filtered;

            const map = Object.fromEntries(
                filtered.map(l => [l.id, l])
            );

            Alpine.store('locations').list = filtered;
            Alpine.store('locations').map = map;
            Alpine.store('locations').suburbPostcodeText =
                this.suburbPostcodeText;
        },

        toKebabCase(str) {
            return str?.trim().toLowerCase().replace(/\s+/g, '-') ?? '';
        },

        getDistance(lat1, lng1, lat2, lng2) {
            const toRad = x => (x * Math.PI) / 180;
            const R = 6371;

            const dLat = toRad(lat2 - lat1);
            const dLon = toRad(lng2 - lng1);

            const a =
                Math.sin(dLat / 2) ** 2 +
                Math.cos(toRad(lat1)) *
                    Math.cos(toRad(lat2)) *
                    Math.sin(dLon / 2) ** 2;

            const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

            return R * c;
        },

        submit() {
            this.applyFilters();
        }
    };
}

function postcodeInput(url) {
    return {
        suburbPostcode: '',
        suburbPostcodeText: '',
        suburbLatLng: null,
        pendingRadius: '',

        init() {
            // Initialize Google Autocomplete
            if (window.google && this.$refs.postcodeInput) {
                this.initAutocomplete();
            } else {
                const interval = setInterval(() => {
                    if (window.google && this.$refs.postcodeInput) {
                        clearInterval(interval);
                        this.initAutocomplete();
                    }
                }, 100);
            }

            // Restore text if preloaded
            if (this.suburbPostcodeText && this.$refs.postcodeInput) {
                this.$refs.postcodeInput.value = this.suburbPostcodeText;
            }
        },

        initAutocomplete() {
            const autocomplete = new google.maps.places.Autocomplete(
                this.$refs.postcodeInput,
                {
                    types: ['(regions)'],
                    componentRestrictions: { country: 'AU' }
                }
            );

            autocomplete.addListener('place_changed', () => {
                const place = autocomplete.getPlace();
                if (place.geometry) {
                    this.suburbLatLng = {
                        lat: place.geometry.location.lat(),
                        lng: place.geometry.location.lng()
                    };

                    this.suburbPostcodeText = place.formatted_address || ''; // <-- save display text
                }
            });

            // If restoring text from query
            if (this.suburbPostcodeText && this.$refs.postcodeInput) {
                this.$refs.postcodeInput.value = this.suburbPostcodeText;
            }
        },

        hasSuburb() {
            return !!(this.suburbPostcodeText);
        },

        submit() {
            const params = new URLSearchParams();
            if (this.suburbPostcodeText) params.set('suburb', this.suburbPostcodeText);
            if (this.suburbLatLng) {
                params.set('lat', this.suburbLatLng.lat);
                params.set('lng', this.suburbLatLng.lng);
            }
            if (this.pendingRadius) params.set('radius', this.pendingRadius);

            const newUrl = `${url}?${params.toString()}`;
            window.location.href = newUrl;
        }
    };
}

// Register Alpine
document.addEventListener('alpine:init', () => {
    Alpine.store('locations', {
        list: [],
        map: {},
        suburbPostcodeText: '',
        pendingSuburbPostcodeText: '',
    });

    Alpine.data('locationsFilter', (locations = []) =>
        locationsFilter(locations)
    );

    Alpine.data('postcodeInput', (url) =>
        postcodeInput(url)
    );
});
